// keyword_pop — uma palavra grande que salta (back easing + blur) sobre um marca-texto
// na cor de destaque que varre da esquerda, faíscas a lápis e linha de apoio embaixo.
import React from 'react';
import { interpolate } from 'remotion';
import type { KeywordPopProps } from './schema';
import { EASE_BACK, EASE_OUT, FONT, PencilPath, Pop, SAFE_W, TEXT_SHADOW, fitFont, onAccent, prog, textWidth, useGFrame, type GraphicComponentProps } from './common';

export const KeywordPop: React.FC<GraphicComponentProps<KeywordPopProps>> = ({ props, accent }) => {
  const f = useGFrame();
  const word = props.word.toUpperCase();
  const size = fitFont(word, { weight: 900, max: 168, min: 80, maxWidth: SAFE_W - 90, letterSpacing: -3 });
  const tw = textWidth(word, size, 900) - 3 * word.length;
  const padX = Math.round(size * 0.26);
  const boxW = tw + padX * 2;
  const boxH = Math.round(size * 1.18);

  const sweep = prog(f, 1, 8, EASE_OUT);
  const pop = prog(f, 2, 10, EASE_BACK);
  const blur = (1 - prog(f, 2, 8, EASE_OUT)) * 14;
  const subIn = prog(f, 8, 16, EASE_OUT);
  const subSize = props.sub ? fitFont(props.sub, { weight: 800, max: 60, min: 38, maxWidth: SAFE_W - 40 }) : 0;
  const breathe = 1 + 0.012 * Math.sin(f * 0.16);

  // três traços curtos saindo do canto superior direito
  const sx = boxW / 2 + 18;
  const sy = -boxH / 2 - 6;
  const spark = [
    `M ${sx} ${sy + 10} L ${sx + 34} ${sy - 18}`,
    `M ${sx + 14} ${sy + 38} L ${sx + 56} ${sy + 30}`,
    `M ${sx - 18} ${sy - 4} L ${sx - 12} ${sy - 44}`,
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', fontFamily: FONT }}>
      <Pop at={3} />
      <div style={{ position: 'relative', width: boxW, height: boxH, scale: String(breathe) }}>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: 18,
            background: accent,
            transformOrigin: 'left center',
            transform: `skewX(-6deg) scaleX(${sweep.toFixed(4)})`,
            boxShadow: '0 16px 40px rgba(0,0,0,0.45)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 900,
            fontSize: size,
            letterSpacing: -3,
            lineHeight: 1,
            color: onAccent(accent),
            whiteSpace: 'nowrap',
            paddingTop: size * 0.04,
            opacity: Math.min(1, pop * 1.6),
            scale: String(interpolate(pop, [0, 1], [0.72, 1])),
            filter: `blur(${blur.toFixed(2)}px)`,
          }}
        >
          {word}
        </div>
        <svg style={{ position: 'absolute', left: '50%', top: '50%', width: 1, height: 1, overflow: 'visible' }}>
          {spark.map((d, i) => (
            <PencilPath key={i} d={d} progress={prog(f, 8 + i * 2, 14 + i * 2, EASE_OUT)} color="#fff" width={8} />
          ))}
        </svg>
      </div>
      {props.sub ? (
        <div
          style={{
            marginTop: 22,
            fontWeight: 800,
            fontSize: subSize,
            color: '#fff',
            textShadow: TEXT_SHADOW,
            WebkitTextStroke: '2px rgba(0,0,0,0.55)',
            paintOrder: 'stroke fill',
            opacity: subIn,
            filter: `blur(${((1 - subIn) * 8).toFixed(2)}px)`,
            translate: `0px ${((1 - subIn) * 14).toFixed(1)}px`,
            whiteSpace: 'nowrap',
          }}
        >
          {props.sub}
        </div>
      ) : null}
    </div>
  );
};
